import {useMemo, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {Checkbox, Spin} from 'antd';
import {Button} from '@dabafinance/react-components';
import {toast} from 'react-toastify';
import {GET_PICKS, PUBLISH_RECOMMENDATION} from '../resolvers';
import {
  MAX_PICKS_PER_REPORT,
  canGenerateReport,
  isAnalysisEmpty,
  isGqlError,
  languageLabel,
  readMutation,
  otherLanguage,
  sourceLabel,
  typeLabel,
} from '../constants';
import styles from './workspace.module.scss';

const PASS = 'pass';
const FAIL = 'fail';
const WARN = 'warn';
const INFO = 'info';

/** One row of the review checklist. */
const Check = ({status, label, detail}) => (
  <li className={styles.checkRow}>
    <span className={styles[`check_${status}`]}>
      {status === PASS ? '✓' : status === FAIL ? '✕' : '!'}
    </span>
    <div>
      <span className={styles.primaryText}>{label}</span>
      {detail && <span className={styles.mutedText}>{detail}</span>}
    </div>
  </li>
);

/**
 * Publish review.
 *
 * The backend only requires one registered source, which is a much weaker bar
 * than a recommendation users should receive a notification about — so this
 * dialog enforces the product checklist and only lets missing reports through
 * on an explicit acknowledgement.
 */
export const PublishModal = ({recommendation, onClose, onPublished}) => {
  const [acceptMissingReports, setAcceptMissingReports] = useState(false);
  const defaultSource = recommendation?.defaultSource;

  const [publishRecommendation, {loading: publishing}] = useMutation(
    PUBLISH_RECOMMENDATION,
    {errorPolicy: 'all'},
  );

  // Three queries — one per type for the default source only. There is no
  // all-source/all-type summary endpoint, and querying every source here would
  // turn the review into an N+1.
  const buy = useQuery(GET_PICKS, {
    variables: {
      recommendationId: recommendation?.id,
      source: defaultSource,
      type: 'BUY',
      page: 1,
      limit: MAX_PICKS_PER_REPORT,
    },
    skip: !defaultSource,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const hold = useQuery(GET_PICKS, {
    variables: {
      recommendationId: recommendation?.id,
      source: defaultSource,
      type: 'HOLD',
      page: 1,
      limit: MAX_PICKS_PER_REPORT,
    },
    skip: !defaultSource,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const sell = useQuery(GET_PICKS, {
    variables: {
      recommendationId: recommendation?.id,
      source: defaultSource,
      type: 'SELL',
      page: 1,
      limit: MAX_PICKS_PER_REPORT,
    },
    skip: !defaultSource,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const loading = buy.loading || hold.loading || sell.loading;

  const groups = useMemo(() => {
    const read = (query, type) => {
      const result = query.data?.getInstrumentRecommendationPicks;
      const payload = isGqlError(result) ? null : result;
      return {
        type,
        picks: payload?.data?.instrumentRecommendation || [],
        reports: payload?.data?.reports || [],
        total: payload?.pagination?.total || 0,
      };
    };
    return [read(buy, 'BUY'), read(hold, 'HOLD'), read(sell, 'SELL')];
  }, [buy, hold, sell]);

  const allPicks = groups.flatMap(group => group.picks);
  const nonEmptyGroups = groups.filter(group => group.picks.length);

  const checks = useMemo(() => {
    const metadataComplete =
      Boolean(recommendation?.name) &&
      Boolean(recommendation?.date) &&
      Boolean(recommendation?.exchange?.id);
    const sources = recommendation?.availableSources || [];
    const defaultRegistered = sources.some(
      entry => entry.name === defaultSource,
    );

    const mismatched = allPicks.filter(
      pick => pick.exchange?.id !== recommendation?.exchange?.id,
    );
    const incomplete = allPicks.filter(
      pick =>
        !(pick.priceAsOf > 0) ||
        !pick.priceAsOfDate ||
        !(pick.projectedPriceBefore > 0) ||
        !pick.projectedPriceBeforeDate ||
        isAnalysisEmpty(pick.analysis),
    );
    const untranslated = allPicks.filter(
      pick =>
        !pick.multiLang?.some(
          entry =>
            entry.language === otherLanguage(pick.language) &&
            !isAnalysisEmpty(entry.analysis),
        ),
    );
    const reportableGroups = nonEmptyGroups.filter(group =>
      canGenerateReport(group.type),
    );
    const groupsMissingReports = reportableGroups.filter(
      group => !group.reports.length,
    );
    const oversizedGroups = groups.filter(
      group => group.total > MAX_PICKS_PER_REPORT,
    );
    const holdGroup = groups.find(group => group.type === 'HOLD');

    return [
      {
        key: 'metadata',
        blocking: true,
        status: metadataComplete ? PASS : FAIL,
        label: 'Name, date and exchange are set',
      },
      {
        key: 'sources',
        blocking: true,
        status: sources.length ? PASS : FAIL,
        label: 'At least one source is registered',
        detail: sources.map(entry => sourceLabel(entry.name)).join(', '),
      },
      {
        key: 'default',
        blocking: true,
        status: defaultSource && defaultRegistered ? PASS : FAIL,
        label: 'Default source exists and is registered',
        detail: defaultSource
          ? sourceLabel(defaultSource)
          : 'No default source set',
      },
      {
        key: 'picks',
        blocking: true,
        status: allPicks.length ? PASS : FAIL,
        label: 'The default source has picks',
        detail: nonEmptyGroups
          .map(group => `${typeLabel(group.type)}: ${group.total}`)
          .join(' · '),
      },
      {
        key: 'exchange',
        blocking: true,
        status: mismatched.length ? FAIL : PASS,
        label: 'Every pick is on the recommendation exchange',
        detail: mismatched.length
          ? `${mismatched.length} picks are on another exchange`
          : recommendation?.exchange?.code,
      },
      {
        key: 'complete',
        blocking: true,
        status: incomplete.length ? FAIL : PASS,
        label: 'Prices, dates and analyses are complete',
        detail: incomplete.length
          ? `${incomplete.length} picks are missing values`
          : undefined,
      },
      {
        key: 'translations',
        blocking: true,
        status: untranslated.length ? FAIL : PASS,
        label: 'Every pick is translated',
        detail: untranslated.length
          ? `${untranslated.length} picks have no ${languageLabel(
              otherLanguage(recommendation?.language),
            )} analysis`
          : undefined,
      },
      {
        key: 'reports',
        blocking: false,
        status: groupsMissingReports.length ? WARN : PASS,
        label: 'Buy and Sell reports have been generated',
        detail: groupsMissingReports.length
          ? `No report yet for ${groupsMissingReports
              .map(group => typeLabel(group.type))
              .join(', ')}`
          : undefined,
      },
      ...(oversizedGroups.length
        ? [
            {
              key: 'limit',
              blocking: false,
              status: WARN,
              label: `Some groups exceed the ${MAX_PICKS_PER_REPORT}-pick report limit`,
              detail: oversizedGroups
                .map(group => `${typeLabel(group.type)}: ${group.total}`)
                .join(' · '),
            },
          ]
        : []),
      ...(holdGroup?.picks.length
        ? [
            {
              key: 'hold',
              blocking: false,
              status: INFO,
              label: 'Hold picks publish without a PDF',
              detail:
                'No active Hold report template exists yet — this does not block publication.',
            },
          ]
        : []),
    ];
  }, [recommendation, defaultSource, allPicks, nonEmptyGroups, groups]);

  const blockingFailures = checks.filter(
    check => check.blocking && check.status === FAIL,
  );
  const reportsMissing =
    checks.find(check => check.key === 'reports')?.status === WARN;
  const canPublish =
    !loading &&
    !blockingFailures.length &&
    (!reportsMissing || acceptMissingReports);

  const handlePublish = () => {
    publishRecommendation({variables: {id: recommendation.id}})
      .then(result => {
        const {payload, error: failure} = readMutation(
          result,
          'publishInstrumentRecommendation',
        );
        if (failure) return toast.error(failure);
        toast.success(payload.message);
        onPublished?.();
        onClose();
      })
      .catch(error => toast.error(error?.message));
  };

  return (
    <div className={styles.modal}>
      <h1>Publish recommendation</h1>
      <p className={styles.modalSubtitle}>
        {recommendation?.name} · reviewed against the default source,{' '}
        {sourceLabel(defaultSource)}
      </p>

      {loading ? (
        <div className={styles.centered}>
          <Spin />
        </div>
      ) : (
        <ul className={styles.checkList}>
          {checks.map(check => (
            <Check
              key={check.key}
              status={check.status}
              label={check.label}
              detail={check.detail}
            />
          ))}
        </ul>
      )}

      {reportsMissing && (
        <Checkbox
          className={styles.override}
          checked={acceptMissingReports}
          onChange={event => setAcceptMissingReports(event.target.checked)}>
          Publish anyway without the missing reports
        </Checkbox>
      )}

      <p className={styles.mutedText}>
        Publishing notifies Daba Pro users. There is no unpublish action, so
        this cannot be reversed from the admin.
      </p>

      <div className={styles.formFooter}>
        <Button label="Cancel" type="secondary" onClick={onClose} />
        <Button
          label={publishing ? 'Publishing…' : 'Publish'}
          disabled={!canPublish || publishing}
          onClick={handlePublish}
        />
      </div>
    </div>
  );
};

export default PublishModal;
